import {
  getDateValue,
  getValidEndDate,
  getValidStartDate,
  remoteRequestFailure,
  remoteRequestInitialState,
  remoteRequestLoading,
  remoteRequestSuccess,
  remoteResourceInitialState,
  remoteResourceLoadFailure,
  remoteResourceLoadSuccess,
  remoteResourceLoading,
  countConciliationDetailErrors,
  countConciliationHeaderStates,
} from '../../helpers';
import { getFilterApplied } from './helpers';
import { ActionType, Actions, State } from './types';

const initialState: State = {
  filters: {
    country: null,
    countryCode: '',
    stores: [],
    franchiseTypes: [],
    federativeEntities: [],
    pos: [],
    segments: [],
    startDate: getDateValue(null),
    endDate: getDateValue(null),
    startBusinessDate: getDateValue(null),
    endBusinessDate: getDateValue(null),
    startOperationDate: getDateValue(null),
    endOperationDate: getDateValue(null),
    startAccountingDate: getDateValue(null),
    endAccountingDate: getDateValue(null),
    thirdPartySalesByItemsGroupBy: ['countryAcronym', 'storeAcronym', 'plu'],
    pod: [],
    differenceType: '',
    accountingEntriesFields: false,
    conciliationDetailFilters: {
      stores: [],
      startDate: getDateValue(null),
      endDate: getDateValue(null),
      conciliationDetailErrors: [],
    },
    conciliationHeaderFilters: {
      conciliationHeaderId: '',
      conciliationHeaderState: [],
    },
  },
  filterApplied: [],
  opStructuresFilters: {
    agreement: [],
    regionalMgmt: [],
    region: [],
    management: [],
    supervision: [],
    company: [],
  },
  mastersImportFilters: {
    countries: [],
    startImportDate: getDateValue(null),
    endImportDate: getDateValue(null),
    state: [],
  },
  posOptions: remoteResourceInitialState([]),
  conciliationDetail: remoteResourceInitialState([]),
  conciliationHeader: remoteResourceInitialState([]),
  ConciliationHeaderStateOptions: remoteResourceInitialState([]),
  ConciliationDetailErrorsOptions: remoteResourceInitialState([]),
  conciliationDetailMeta: {
    errorsCounts: countConciliationDetailErrors([]),
    filterByError: [],
  },
  conciliationHeaderMeta: {
    stateCounts: countConciliationHeaderStates([]),
    filterByState: [],
  },
  mastersImport: remoteResourceInitialState([]),
  mastersTypes: remoteResourceInitialState([]),
  mastersImportExecution: remoteRequestInitialState,
  fiscalDetail: remoteResourceInitialState([]),
  fiscalDetailFilters: {
    countries: [],
    stores: [],
    startDate: getDateValue(null),
    endDate: getDateValue(null),
  },
};

const reducer = (state: State = initialState, action: Actions): State => {
  switch (action.type) {
    case ActionType.changeFiltersCountry:
      return {
        ...state,
        posOptions: remoteResourceInitialState([]),
        conciliationDetail: remoteResourceInitialState([]),
        conciliationDetailMeta: {
          ...state.conciliationDetailMeta,
          errorsCounts: countConciliationDetailErrors([]),
          filterByError: [],
        },
        conciliationHeader: remoteResourceInitialState([]),
        conciliationHeaderMeta: {
          ...state.conciliationHeaderMeta,
          stateCounts: countConciliationHeaderStates([]),
          filterByState: [],
        },
        filters: {
          ...state.filters,
          ...{
            countryCode: action.countryCode,
            stores: [],
            franchiseTypes: [],
            federativeEntities: [],
            pos: [],
            salesTypes: [],
            hourRange: [],
            timeBand: [],
            segments: [],
            paymentTypes: [],
            pod: [],
            conciliationDetailFilters: {
              ...state.filters.conciliationDetailFilters,
              stores: [],
            },
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'countryCode', action.countryCode),
        opStructuresFilters: {
          agreement: [],
          regionalMgmt: [],
          region: [],
          management: [],
          supervision: [],
          company: [],
        },
      };
    case ActionType.changeFiltersStore:
      return {
        ...state,
        posOptions: remoteResourceInitialState([]),
        conciliationHeader: remoteResourceInitialState([]),
        conciliationHeaderMeta: {
          ...state.conciliationHeaderMeta,
          stateCounts: countConciliationHeaderStates([]),
          filterByState: [],
        },
        filters: {
          ...state.filters,
          franchiseTypes: [],
          federativeEntities: [],
          stores: action.stores,
          pos: [],
        },
        filterApplied: getFilterApplied(state.filterApplied, 'stores', action.stores),
      };
    case ActionType.changeConciliationDetailFiltersStore:
      return {
        ...state,
        conciliationDetail: remoteResourceInitialState([]),
        conciliationDetailMeta: {
          ...state.conciliationDetailMeta,
          errorsCounts: countConciliationDetailErrors([]),
          filterByError: [],
        },
        filters: {
          ...state.filters,
          conciliationDetailFilters: {
            ...state.filters.conciliationDetailFilters,
            stores: action.stores,
          },
        },
      };
    case ActionType.changeConciliationDetailFilterStartAccountingDate:
      return {
        ...state,
        conciliationDetail: remoteResourceInitialState([]),
        conciliationDetailMeta: {
          ...state.conciliationDetailMeta,
          errorsCounts: countConciliationDetailErrors([]),
          filterByError: [],
        },
        filters: {
          ...state.filters,
          conciliationDetailFilters: {
            ...state.filters.conciliationDetailFilters,
            ...{
              startDate: getDateValue(action.date),
              endDate: getDateValue(
                getValidEndDate(action.date, state.filters.conciliationDetailFilters.endDate)
              ),
            },
          },
        },
      };
    case ActionType.changeConciliationDetailFilterEndAccountingDate:
      return {
        ...state,
        conciliationDetail: remoteResourceInitialState([]),
        conciliationDetailMeta: {
          ...state.conciliationDetailMeta,
          errorsCounts: countConciliationDetailErrors([]),
          filterByError: [],
        },
        filters: {
          ...state.filters,
          conciliationDetailFilters: {
            ...state.filters.conciliationDetailFilters,
            ...{
              endDate: getDateValue(action.date),
              startDate: getDateValue(
                getValidStartDate(state.filters.conciliationDetailFilters.startDate, action.date)
              ),
            },
          },
        },
      };
    case ActionType.changeFiltersPos:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...{ pos: action.pos },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'pos', action.pos),
      };
    case ActionType.changeFiltersStoreMotherChild:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...{ pod: action.pod },
        },
      };
    case ActionType.changeFiltersSegments:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...{ segments: action.segments },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'segments', action.segments),
      };
    case ActionType.changeFilterStartBusinessDate:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...{
            startBusinessDate: getDateValue(action.date),
            endBusinessDate: getDateValue(
              getValidEndDate(action.date, state.filters.endBusinessDate)
            ),
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'startBusinessDate', action.date),
      };
    case ActionType.changeFilterEndBusinessDate:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...{
            endBusinessDate: getDateValue(action.date),
            startBusinessDate: getDateValue(
              getValidStartDate(state.filters.startBusinessDate, action.date)
            ),
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'endBusinessDate', action.date),
      };
    case ActionType.changeFilterStartOperationDate:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...{
            startOperationDate: getDateValue(action.date),
            endOperationDate: getDateValue(
              getValidEndDate(action.date, state.filters.endOperationDate)
            ),
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'startOperationDate', action.date),
      };
    case ActionType.changeFilterEndOperationDate:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...{
            endOperationDate: getDateValue(action.date),
            startOperationDate: getDateValue(
              getValidStartDate(state.filters.startOperationDate, action.date)
            ),
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'endOperationDate', action.date),
      };
    case ActionType.changeFilterStartAccountingDate:
      return {
        ...state,
        conciliationHeader: remoteResourceInitialState([]),
        conciliationHeaderMeta: {
          ...state.conciliationHeaderMeta,
          stateCounts: countConciliationHeaderStates([]),
          filterByState: [],
        },
        filters: {
          ...state.filters,
          ...{
            startAccountingDate: getDateValue(action.date),
            endAccountingDate: getDateValue(
              getValidEndDate(action.date, state.filters.endAccountingDate)
            ),
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'startAccountingDate', action.date),
      };
    case ActionType.changeFilterEndAccountingDate:
      return {
        ...state,
        conciliationHeader: remoteResourceInitialState([]),
        conciliationHeaderMeta: {
          ...state.conciliationHeaderMeta,
          stateCounts: countConciliationHeaderStates([]),
          filterByState: [],
        },
        filters: {
          ...state.filters,
          ...{
            endAccountingDate: getDateValue(action.date),
            startAccountingDate: getDateValue(
              getValidStartDate(state.filters.startAccountingDate, action.date)
            ),
          },
        },
        filterApplied: getFilterApplied(state.filterApplied, 'endAccountingDate', action.date),
      };
    case ActionType.findPosRequested:
      return {
        ...state,
        posOptions: remoteResourceLoading([]),
      };
    case ActionType.findPosSuccess:
      return {
        ...state,
        posOptions: remoteResourceLoadSuccess(action.posOptions),
      };
    case ActionType.findPosFailure:
      return {
        ...state,
        posOptions: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.changeOpStrucuresFilterAgreement:
      return {
        ...state,
        opStructuresFilters: {
          ...state.opStructuresFilters,
          ...{ agreement: action.agreement },
        },
      };
    case ActionType.changeOpStrucuresFilterRegionalManagement:
      return {
        ...state,
        opStructuresFilters: {
          ...state.opStructuresFilters,
          ...{ regionalMgmt: action.regionalMgmt },
        },
      };
    case ActionType.changeOpStrucuresFilterRegion:
      return {
        ...state,
        opStructuresFilters: {
          ...state.opStructuresFilters,
          ...{ region: action.region },
        },
      };
    case ActionType.changeOpStrucuresFilterManagement:
      return {
        ...state,
        opStructuresFilters: {
          ...state.opStructuresFilters,
          ...{ management: action.management },
        },
      };
    case ActionType.changeOpStrucuresFilterSupervision:
      return {
        ...state,
        opStructuresFilters: {
          ...state.opStructuresFilters,
          ...{ supervision: action.supervision },
        },
      };
    case ActionType.changeOpStrucuresFilterCompany:
      return {
        ...state,
        opStructuresFilters: {
          ...state.opStructuresFilters,
          ...{ company: action.company },
        },
      };
    case ActionType.changeMasterImportFilterCountry:
      return {
        ...state,
        mastersImport: remoteResourceInitialState([]),
        mastersImportFilters: {
          ...state.mastersImportFilters,
          countries: action.countries,
        },
      };
    case ActionType.changeMasterImportFilterStartImportDate:
      return {
        ...state,
        mastersImport: remoteResourceInitialState([]),
        mastersImportFilters: {
          ...state.mastersImportFilters,
          startImportDate: getDateValue(action.date),
        },
      };
    case ActionType.changeMasterImportFilterEndImportDate:
      return {
        ...state,
        mastersImport: remoteResourceInitialState([]),
        mastersImportFilters: {
          ...state.mastersImportFilters,
          endImportDate: getDateValue(action.date),
        },
      };
    case ActionType.changeMasterImportFilterState:
      return {
        ...state,
        mastersImport: remoteResourceInitialState([]),
        mastersImportFilters: {
          ...state.mastersImportFilters,
          state: action.state,
        },
      };
    case ActionType.findMastersImportRequested:
      return {
        ...state,
        mastersImport: remoteResourceLoading([]),
      };
    case ActionType.findMastersImportSuccess:
      return {
        ...state,
        mastersImport: remoteResourceLoadSuccess(action.mastersImport),
      };
    case ActionType.findMastersImportFailure:
      return {
        ...state,
        mastersImport: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.findMastersTypesRequested:
      return {
        ...state,
        mastersTypes: remoteResourceLoading([]),
      };
    case ActionType.findMastersTypesSuccess:
      return {
        ...state,
        mastersTypes: remoteResourceLoadSuccess(action.mastersTypes),
      };
    case ActionType.findMastersTypesFailure:
      return {
        ...state,
        mastersTypes: remoteResourceLoadFailure([], action.error),
      };

    case ActionType.mastersImportExecutionRequested:
      return {
        ...state,
        mastersImportExecution: remoteRequestLoading,
      };
    case ActionType.mastersImportExecutionSuccess:
      return {
        ...state,
        mastersImportExecution: remoteRequestSuccess,
      };
    case ActionType.mastersImportExecutionFailure:
      return {
        ...state,
        mastersImportExecution: remoteRequestFailure(action.error),
      };
    case ActionType.findFiscalDetailRequested:
      return {
        ...state,
        fiscalDetail: remoteResourceLoading([]),
      };
    case ActionType.findFiscalDetailSuccess:
      return {
        ...state,
        fiscalDetail: remoteResourceLoadSuccess(action.fiscalDetail),
      };
    case ActionType.findFiscalDetailFailure:
      return {
        ...state,
        fiscalDetail: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.changeFiscalDetailFilterCountry:
      return {
        ...state,
        fiscalDetail: remoteResourceInitialState([]),
        fiscalDetailFilters: {
          ...state.fiscalDetailFilters,
          countries: action.countries,
          stores: [],
        },
      };
    case ActionType.changeFiscalDetailFilterStore:
      return {
        ...state,
        fiscalDetail: remoteResourceInitialState([]),
        fiscalDetailFilters: {
          ...state.fiscalDetailFilters,
          stores: action.stores,
        },
      };
    case ActionType.changeFiscalDetailFilterStartDate:
      return {
        ...state,
        fiscalDetail: remoteResourceInitialState([]),
        fiscalDetailFilters: {
          ...state.fiscalDetailFilters,
          startDate: getDateValue(action.date),
        },
      };
    case ActionType.changeFiscalDetailFilterEndDate:
      return {
        ...state,
        fiscalDetail: remoteResourceInitialState([]),
        fiscalDetailFilters: {
          ...state.fiscalDetailFilters,
          endDate: getDateValue(action.date),
        },
      };
    case ActionType.findConciliationDetailRequested:
      return {
        ...state,
        conciliationDetail: remoteResourceLoading([]),
        conciliationDetailMeta: {
          ...state.conciliationDetailMeta,
          errorsCounts: countConciliationDetailErrors([]),
          filterByError: [],
        },
      };
    case ActionType.findConciliationDetailSuccess:
      return {
        ...state,
        conciliationDetail: remoteResourceLoadSuccess(action.conciliationDetail),
        conciliationDetailMeta: {
          ...state.conciliationDetailMeta,
          errorsCounts: action.errorsCounts,
          filterByError: [],
        },
      };
    case ActionType.findConciliationDetailFailure:
      return {
        ...state,
        conciliationDetail: remoteResourceLoadFailure([], action.error),
        conciliationDetailMeta: {
          ...state.conciliationDetailMeta,
          errorsCounts: countConciliationDetailErrors([]),
          filterByError: [],
        },
      };
    case ActionType.changeConciliationDetailFilterByErrorFilter: {
      const current = state.conciliationDetailMeta.filterByError;
      const toggled = current.includes(action.filterByError)
        ? current.filter(e => e !== action.filterByError)
        : [...current, action.filterByError];
      return {
        ...state,
        conciliationDetailMeta: {
          ...state.conciliationDetailMeta,
          filterByError: toggled,
        },
      };
    }
    case ActionType.findConciliationHeaderRequested:
      return {
        ...state,
        conciliationHeader: remoteResourceLoading([]),
        conciliationHeaderMeta: {
          ...state.conciliationHeaderMeta,
          stateCounts: countConciliationHeaderStates([]),
          filterByState: [],
        },
      };
    case ActionType.findConciliationHeaderSuccess:
      return {
        ...state,
        conciliationHeader: remoteResourceLoadSuccess(action.conciliationHeader),
        conciliationHeaderMeta: {
          ...state.conciliationHeaderMeta,
          stateCounts: action.statesCounts,
        },
      };
    case ActionType.findConciliationHeaderFailure:
      return {
        ...state,
        conciliationHeader: remoteResourceLoadFailure([], action.error),
      };
    case ActionType.changeConciliationHeaderFilterByStateFilter: {
      const current = state.conciliationHeaderMeta.filterByState;
      const toggled = current.includes(action.filterByState)
        ? current.filter(s => s !== action.filterByState)
        : [...current, action.filterByState];
      return {
        ...state,
        conciliationHeaderMeta: {
          ...state.conciliationHeaderMeta,
          filterByState: toggled,
        },
      };
    }
    case ActionType.changeFilterConciliationDetailErrors:
      return {
        ...state,
        conciliationDetail: remoteResourceInitialState([]),
        conciliationDetailMeta: {
          ...state.conciliationDetailMeta,
          errorsCounts: countConciliationDetailErrors([]),
          filterByError: [],
        },
        filters: {
          ...state.filters,
          conciliationDetailFilters: {
            ...state.filters.conciliationDetailFilters,
            conciliationDetailErrors: action.causeTypes,
          },
        },
      };
    case ActionType.changeFilterConciliationHeaderState:
      return {
        ...state,
        conciliationHeader: remoteResourceInitialState([]),
        conciliationHeaderMeta: {
          ...state.conciliationHeaderMeta,
          stateCounts: countConciliationHeaderStates([]),
          filterByState: [],
        },
        filters: {
          ...state.filters,
          conciliationHeaderFilters: {
            ...state.filters.conciliationHeaderFilters,
            conciliationHeaderState: action.causeTypes,
          },
        },
      };
    case ActionType.changeFranchiseTypeFilter:
      return {
        ...state,
        conciliationHeader: remoteResourceInitialState([]),
        conciliationHeaderMeta: {
          ...state.conciliationHeaderMeta,
          stateCounts: countConciliationHeaderStates([]),
          filterByState: [],
        },
        filters: {
          ...state.filters,
          franchiseTypes: action.franchiseTypes,
        },
      };
    case ActionType.changeFilterFederativeEntities:
      return {
        ...state,
        conciliationHeader: remoteResourceInitialState([]),
        conciliationHeaderMeta: {
          ...state.conciliationHeaderMeta,
          stateCounts: countConciliationHeaderStates([]),
          filterByState: [],
        },
        filters: {
          ...state.filters,
          federativeEntities: action.federativeEntities,
        },
      };
    case ActionType.changeConciliationHeaderId:
      return {
        ...state,
        filters: {
          ...state.filters,
          conciliationHeaderFilters: {
            ...state.filters.conciliationHeaderFilters,
            conciliationHeaderId: action.conciliationHeaderId,
          },
        },
      };
    default:
      return state;
  }
};

export default reducer;
